import React, { useState, useEffect } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import { fetchWorkEducation } from '../../../api/workEducationApi';

// Helper function to unescape HTML entities
const unescapeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.documentElement.textContent;
};

function TimelineSection() {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    const fetchTimelineData = async () => {
      try {
        setLoading(true);
        const data = await fetchWorkEducation({ paginate: false });

        if (data.success) {
          const formattedData = data.data.map(item => {
            const title = item.designation || item.degree || 'N/A';
            let organization = item.company_name || item.institution_name || 'N/A';

            let durationString = '';
            if (item.start_month && item.start_year) {
              durationString += `${item.start_month} ${item.start_year}`;
            } else if (item.start_year) {
              durationString += `${item.start_year}`;
            }

            if (item.end_year) {
              if (item.end_month) {
                durationString += ` - ${item.end_month} ${item.end_year}`;
              } else {
                durationString += ` - ${item.end_year}`;
              }
            } else {
              durationString += ` - Present`;
            }

            return {
              id: item.id,
              type: item.designation ? 'PROFESSIONAL EXPERIENCE' : 'EDUCATION',
              title: title,
              organization: organization,
              duration: durationString,
              // Unescape HTML content here
              description: item.content ? unescapeHtml(item.content) : '',
            };
          });
          setTimelineData(formattedData);
        } else {
          addToast('error', data.message || 'Failed to fetch timeline data.');
        }
      } catch (e) {
        console.error("Error fetching timeline data:", e);
        addToast('error', 'Failed to load timeline data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTimelineData();
  }, [addToast]);

  return (
    <section className="py-16">
      <h2 className="text-4xl font-bold text-center text-text-white mb-16 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">
        Education & Experience
      </h2>
      <div className="relative border-l-2 border-accent-red ml-4 md:ml-12">
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="mb-8 flex items-start w-full animate-pulse">
              <div className="absolute w-4 h-4 bg-gray-700 rounded-full -left-2 border-2 border-bg-dark"></div>
              <div className="ml-8 p-6 bg-bg-light rounded-lg shadow-md border border-border-subtle w-full">
                <div className="h-6 bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-4 bg-gray-700 rounded w-full mb-1"></div>
                <div className="h-4 bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          ))
        ) : timelineData.length > 0 ? (
          timelineData.map((item) => (
            <div key={item.id} className="mb-8 flex items-start w-full">
              <div className="absolute w-4 h-4 bg-accent-red rounded-full -left-2 border-2 border-bg-dark"></div>
              <div className="ml-8 p-6 bg-bg-light rounded-lg shadow-md border border-border-subtle w-full">
                <h3 className="text-xl font-semibold text-text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-accent-red mb-2">
                  {item.organization} | {item.duration}
                </p>
                {item.description && ( // Check if description exists
                  <div
                    className="prose prose-invert max-w-none" // Add prose classes for basic styling of HTML
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                )}
              </div>
            </div>
          ))
        ) : (
          <p className="text-center">No education or experience entries found.</p>
        )}
      </div>
    </section>
  );
}

export default TimelineSection;